import { AfterViewChecked, Component, OnInit } from '@angular/core'
import { ActivatedRoute } from '@angular/router'
import { getLoggedUser } from 'src/app/_helpers/LocalStorage'
import { arrayPartition, deepcopy, removeToolTip } from 'src/app/_helpers/Utils'
import { ViewValidation } from 'src/app/_helpers/ViewValidation'
import { IHandlerOperation } from 'src/app/models/HandlerOperation'
import { Answer, AppraisalReport } from 'src/app/models/Reports'
import { ISpecies } from 'src/app/models/Species'
import { UserAdmin } from 'src/app/models/UserAdmin'
import { DemandService } from 'src/app/services/demand.service'
import { EvaluationService } from 'src/app/services/evaluation.service'
import { HandlerOperationService } from 'src/app/services/handler.operation.service'
import { NotificationService } from 'src/app/services/notification.service'
import { startupPuppeteerLoading } from 'src/app/services/puppeteer-server.service'
import { SpeciesService } from 'src/app/services/species.service'
import { TreeService } from 'src/app/services/tree.service'

@Component({
	selector: 'app-appraisal-report',
	templateUrl: './appraisal-report.component.html'
})
export class AppraisalReportComponent implements OnInit, AfterViewChecked {

	constructor(
		private readonly routeActive: ActivatedRoute,
		private readonly demandService: DemandService,
		private readonly evaluationService: EvaluationService,
		private readonly handlerOperationService: HandlerOperationService,
		private readonly speciesService: SpeciesService,
		private readonly treeService: TreeService,
		private readonly notificationService: NotificationService
	) {}

	public ViewValidation: ViewValidation = new ViewValidation()

	public companyId: string = localStorage.getItem('companyId')
	public sessionUser: UserAdmin = getLoggedUser()
	public isAdmin: boolean = this.sessionUser.roles.includes('GENERAL_ADMIN')

	public reports: Array<AppraisalReport> = []
	public species: Array<ISpecies> = []
	public handlerOperations: Array<IHandlerOperation> = []
	public oldReportJson: Array<{
		appraisalId: string,
		bodyReport: string
	}> = []
	public appraisalIds: Array<string> = []
	public demandId: string = ''
	public changeRequesterIndexSelected: number = 0

	public allAppraisalsIsNotDone: boolean = false
	public allAppraisalsIsNotAssign: boolean = false
	public isLoading: boolean = true
	public enableAssignButton: boolean = false
	public enableEditButton: boolean = false
	public enableSaveButton: boolean = false
	public editModeActive: boolean = false
	public openModalAssign: boolean = false
	public openModalChangeRequester: boolean = false
	public disableChangeRequestInformationButton: boolean = true
	public openModalChoosePrint: boolean = false
	public showAddressChangeModal: boolean = false
	public someReportChangedAddress: boolean = false

	// Native Metods from AngularCore Implements

	public async ngOnInit(): Promise<void> {
		this.demandId = this.routeActive.snapshot.queryParamMap.get('demandId')
		const ids: string = this.routeActive.snapshot.paramMap.get('appraisalIds')

		this.appraisalIds = ids.split(',')

		await this.getReports(this.appraisalIds)
		await this.getSpecies()
		await this.getHandlerOperations()
	}

	public ngAfterViewChecked() {
		const paginations =
			document.getElementsByClassName('pagination')

		Array.from(paginations).forEach((pagination, index) => {
			const thisPage: HTMLSpanElement =
				pagination.getElementsByClassName('this-page')[0] as HTMLSpanElement

			const totPage: HTMLSpanElement =
				pagination.getElementsByClassName('tot-pages')[0] as HTMLSpanElement

			thisPage.innerText = Number(index + 1).toString()
			totPage.innerText = paginations.length.toString()
		})
	}

	// Get Data For Load Reports

	public async getReports(ids: Array<string>) {

		const parts = arrayPartition(ids)

		for (const part of parts) {
			try {
				const reports: any =
					await this.demandService.getEvaluationReportArboreal(part, this.demandId).toPromise()
				this.reports = [...this.reports, ...reports]
			} catch (err) {
				console.log('Error', err)
			}
		}

		const userIsGeneralAdmin: boolean =
			this.sessionUser.roles.includes('GENERAL_ADMIN')

		const userIsOwnerAllAppraisals: boolean = this.reports.every(report =>
			report.responsible.idMongo === this.sessionUser._id
		)

		const hasPermitionUpdate: boolean =
			this.sessionUser.assignments.includes('APPRAISAL_REPORT_UPDATE')

		const hasPermitionUpdateAll: boolean =
			this.sessionUser.assignments.includes('APPRAISAL_REPORT_UPDATE_ALL')

		const hasPermitionAssignAll: boolean =
			this.sessionUser.assignments.includes('APPRAISAL_HISTORY_ASSIGN_COMMUNICATION')

		this.allAppraisalsIsNotDone = this.reports.every(report =>
			!report.appraisalDone
		)

		this.allAppraisalsIsNotAssign = this.reports.every(report =>
			!report.assign
		)

		this.enableAssignButton = Boolean(
			userIsOwnerAllAppraisals ||
			hasPermitionAssignAll ||
			userIsGeneralAdmin
		)

		this.enableEditButton = Boolean(
			this.allAppraisalsIsNotDone &&
			this.allAppraisalsIsNotAssign &&
			(
				(userIsOwnerAllAppraisals && hasPermitionUpdate) ||
				hasPermitionUpdateAll ||
				userIsGeneralAdmin
			)
		)

		this.reports.forEach(report => {
			if (this.enableEditButton) {
				const { qrCodeImage, ...responseWithoutQrcodeImage } = report

				this.oldReportJson.push({
					appraisalId: report.idMongo,
					bodyReport: JSON.stringify(responseWithoutQrcodeImage)
				})
			}
		})
		startupPuppeteerLoading(document)
	}

	public async getSpecies(): Promise<void> {
		const species =
			await this.speciesService.getSpeciesSimple().toPromise()

		this.verifySpecies()

		const arrayManipuleSpecies = [...species]

		this.species =
			arrayManipuleSpecies.sort((a, b) => {
				if (a.nomenclature.scientificName.toLowerCase() < b.nomenclature.scientificName.toLowerCase()) {
					return -1
				}
				return 1
			})

	}

	public async getHandlerOperations(): Promise<void> {
		const handleOperations: Array<IHandlerOperation> =
			await this.handlerOperationService.list(this.companyId, false, 'Evaluator').toPromise()

		const operations = handleOperations.map(item => {
			return {
				operation: item.operation,
				_id: item._id,
				legalFundamentation: item.enableInRelatory ? item.legalFundamentation : null,
				sections: item.sections
			}
		})

		const operationFoInsert = []

		this.reports.forEach(report => {
			report.handleOperations.forEach(recommendationManagement => {
				if (operations.findIndex(operation => operation._id == recommendationManagement.idMongo) === -1) {
					operationFoInsert.push({
						_id: recommendationManagement.idMongo,
						operation: recommendationManagement.operation,
						legalFundamentation: recommendationManagement.legalFundamentation,
						disabled: true
					})
				}
			})
		})

		this.handlerOperations = [...operations, ...operationFoInsert]
	}

	public verifySpecies() {
		this.reports.forEach(report => {
			const specieActive = this.species.some(specie =>
				specie._id === report.tree.specieId
			)

			if (!specieActive) {
				this.insertSpecieInSpeciesArray(
					report.tree.specieId,
					report.tree.specie,
					report.tree.popularName,
					report.tree.family,
					report.tree.source,
					report.tree.specialCategory
				)
			}
		})
	}

	public insertSpecieInSpeciesArray(
		idMongo: string,
		scientificName: string,
		popularName: string,
		botanicalClassification: string,
		origin: string,
		specialCategory: string
	) {
		this.species.push({
			_id: idMongo,
			nomenclature: {
				scientificName,
				popularName,
				botanicalClassification
			},
			characterization: {
				origin
			},
			evaluationComponents: {
				specialCategory
			},
			inactive: true
		})
	}

	// Handles Metods Reports

	public async updateReport(): Promise<void> {

		const dataEvaluationsEditing: Array<AppraisalReport> = deepcopy(this.reports)

		dataEvaluationsEditing.forEach(report => {
			report.oldReport = this.oldReportJson.find(oldReport =>
				oldReport.appraisalId === report.idMongo
			).bodyReport

			report.qrCodeImage = ''
		})

		try {
			const response: string =
				await this.evaluationService.updateEvaluationReportArboreal(dataEvaluationsEditing).toPromise()

			this.notificationService.notify(response, 200)
			this.redefineOldReports()

			if (this.someReportChangedAddress) {
				this.someReportChangedAddress = false
				this.addressAlertModalToggle()
			}
		} catch (error) {
			this.notificationService.notify(error.error.message, error.error.statusCode || 400)
		}
	}

	public async assignCommunication() {
		this.openCloseModalAssign()

		try {

			const appraisalIds: Array<string> = []

			this.reports.forEach(report => {
				report.assign = true
				report.responsibleForSign.crea = this.sessionUser.crea
				report.responsibleForSign.name = this.sessionUser.name

				appraisalIds.push(report.idMongo)
			})

			const response: string =
				await this.demandService.assignCommunication(appraisalIds).toPromise()

			this.notificationService.notify(response, 200)
			this.triggerAssignReportEvent()
			this.enableEditButton = false
			this.allAppraisalsIsNotAssign = false
		} catch (error) {
			this.notificationService.notify(error.error.message, error.error.statusCode || 400)
		}
	}

	public changeDemandRequesterInformations() {
		const data =
			this.reports[this.changeRequesterIndexSelected].requester

		this.demandService.updateRequesterData(this.reports[this.changeRequesterIndexSelected].idMongo, data).subscribe(response => {
				this.notificationService.notify(response, 200)
				this.openModalChangeRequester = false
				this.disableChangeRequestInformationButton = false
			}, error => {
				this.notificationService.notify(error.error.message, error.error.statusCode || 400)
			}
		)
	}

	public async exportReport() {
		const type = 'evaluation-report-arboreal'
		const ids = this.reports.map(report => report.idMongo)

		this.treeService.exportReport({type, ids, demandId: this.demandId})

		this.openSelectionModeModal()
	}

	public triggerAssignReportEvent() {
		localStorage.setItem(
			'assignReport',
			JSON.stringify(
				{ message: `The appraisal ${this.reports[0].idMongo} has assign now: `, timestamp: new Date().getTime() }
			)
		)
	}

	// Reports Validations

	public existPhotoInReport(indexReport: number): boolean {
		return Boolean(
			this.reports[indexReport].answers.filter(answer => answer.url).length > 0
		)
	}

	public existMoreThreePhotosInReport(report: AppraisalReport): boolean {
		return Boolean(report.answers.filter(answer => answer.url).length > 3)
	}

	public existSpaceForInsertFooterInThirdPageAndNotImage(indexReport: number): boolean {
		const answers: HTMLSelectElement =
			document.getElementsByClassName('answers')[indexReport] as HTMLSelectElement

		const legalFundamentations: HTMLSelectElement =
			document.getElementsByClassName('legal-fundamentations')[indexReport] as HTMLSelectElement

		if (!answers && !legalFundamentations) {
			return true
		}

		const answerHeight: number = answers.offsetHeight
		const legalFundamentationsHeight: number = legalFundamentations.offsetHeight

		const heightWithoutFooter: number = Number(
			60 + // Altura do Header
			answerHeight +
			legalFundamentationsHeight +
			50 + // Altura do padding no fim da página
			24 // Soma de todos os Gaps
		)

		const totalPageHeight: number = 1526
		const footerHeight: number = 532

		const notImageInReport = !this.existPhotoInReport(indexReport)

		return Boolean(
			(heightWithoutFooter + footerHeight) <= totalPageHeight &&
			notImageInReport
		)
	}

	public existSpaceForInsertFooterInFourPage(indexReport: number): boolean {
		const photoRecords: HTMLSelectElement =
			document.getElementsByClassName('photo-records')[indexReport] as HTMLSelectElement

		if (!photoRecords) {
			return true
		}

		const photoRecordsHeight: number = photoRecords.offsetHeight

		const heightWithoutFooter: number = Number(
			60 + // Altura do Header
			photoRecordsHeight +
			50 + // Altura do padding no fim da página
			8 // Soma de todos os Gaps
		)

		const totalPageHeight: number = 1526
		const footerHeight: number = 532

		return Boolean((heightWithoutFooter + footerHeight) <= totalPageHeight)
	}

	public existSpaceForInsertImagesInThirdPage(indexReport: number): boolean {
		const answers: HTMLSelectElement =
			document.getElementsByClassName('answers')[indexReport] as HTMLSelectElement

		const legalFundamentations: HTMLSelectElement =
			document.getElementsByClassName('legal-fundamentations')[indexReport] as HTMLSelectElement

		if (!answers && !legalFundamentations) {
			return true
		}

		const answerHeight: number = answers.offsetHeight
		const legalFundamentationsHeight: number = legalFundamentations.offsetHeight

		const heightWithoutFooter: number = Number(
			60 + // Altura do Header
			answerHeight +
			legalFundamentationsHeight +
			50 + // Altura do padding no fim da página
			24 // Soma de todos os Gaps
		)

		const totalPageHeight: number = 1526
		const imageHeight: number = 744

		return Boolean((heightWithoutFooter + imageHeight) <= totalPageHeight)
	}

	// Open And Close Modals

	public openCloseModalChangeRequester(appraisalId?: string) {

		if (appraisalId) {
			this.openModalChangeRequester = true

			const indexAppraisalSelected: number =
				this.reports.findIndex(report =>
					report.idMongo == appraisalId
				)

			if (indexAppraisalSelected != -1) {
				this.changeRequesterIndexSelected = indexAppraisalSelected
				this.disableChangeRequestInformationButton =
					this.reports[this.changeRequesterIndexSelected].requester.notEditable
			}
		} else {
			this.openModalChangeRequester = false
			this.disableChangeRequestInformationButton = false
		}

		removeToolTip()
	}

	public openCloseModalAssign() {
		this.openModalAssign = !this.openModalAssign
	}

	public openSelectionModeModal() {
		removeToolTip()
		this.openModalChoosePrint = !this.openModalChoosePrint
	}

	// Other Utilies

	public returnAnswersWithImagesFromRangeIndex(
		answers: Array<Answer>,
		startIndex: number,
		endIndex: number
	) {
		return answers.filter(answer => answer.url).slice(startIndex, endIndex + 1)
	}

	public verifyChangeReports() {
		if (this.oldReportJson.length === 0) {
			return false
		}

		let changesDetected: boolean = false

		this.reports.forEach(report => {
			const { qrCodeImage, ...responseWithoutQrcodeImage } = report

			const originalReport: string = this.oldReportJson.find(oldReport =>
				oldReport.appraisalId == report.idMongo
			).bodyReport

			changesDetected = Boolean(
				originalReport != JSON.stringify(responseWithoutQrcodeImage)
			)
		})

		return changesDetected
	}

	public redefineOldReports() {
		this.oldReportJson.length = 0

		this.reports.forEach(report => {
			const { qrCodeImage, ...responseWithoutQrcodeImage } = report

			this.oldReportJson.push({
				appraisalId: report.idMongo,
				bodyReport: JSON.stringify(responseWithoutQrcodeImage)
			})
		})
	}

	public enableEditMode() {
		this.editModeActive = !this.editModeActive
	}

	public printOut() {
		this.openSelectionModeModal()
		setTimeout(() => {
			window.print()
		}, 200)
	}

	public stopLoading() {
		this.isLoading = false
	}

	public addressWasChanged() {
		this.someReportChangedAddress = true
	}

	public addressAlertModalToggle() {
		this.showAddressChangeModal = !this.showAddressChangeModal
	}
}
